import React, { useState } from 'react';
import { Ear, ChevronRight, ChevronLeft, Volume2, VolumeX } from 'lucide-react';

const TinnitusProfile = ({ formData, updateFormData, t, onSubStepChange, onBackPress }) => {
  const [subStep, setSubStep] = useState(0); // 0: onset, 1: laterality, 2: percept_type
  const [playingSound, setPlayingSound] = useState(null);
  const audioContextRef = React.useRef(null);
  const oscillatorRef = React.useRef(null);
  const gainNodeRef = React.useRef(null);
  
  // Notify parent of sub-step changes
  React.useEffect(() => {
    if (onSubStepChange) {
      onSubStepChange(subStep, 2); // current step, total steps
    }
  }, [subStep, onSubStepChange]);
  
  // Handle back press from parent (header back arrow)
  React.useEffect(() => {
    if (onBackPress) {
      const handleBack = () => {
        if (subStep > 0) {
          setSubStep(prev => prev - 1);
          return true; // Handled internally
        }
        return false; // Let parent handle it (go to previous major step)
      };
      onBackPress(handleBack);
    }
  }, [subStep, onBackPress]);
  
  // Cleanup audio on unmount
  React.useEffect(() => {
    return () => {
      stopSound();
    };
  }, []);
  
  const stopSound = () => {
    if (oscillatorRef.current) {
      try {
        oscillatorRef.current.stop();
        oscillatorRef.current.disconnect();
      } catch (e) {
        // Already stopped
      }
      oscillatorRef.current = null;
    }
    if (gainNodeRef.current) {
      gainNodeRef.current.disconnect();
      gainNodeRef.current = null;
    }
    setPlayingSound(null);
  };
  
  const playSound = (type) => {
    // Stop any currently playing sound
    stopSound();
    
    // Create audio context if needed
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    const ctx = audioContextRef.current;
    const typeData = perceptTypes.find(t => t.value === type.value);
    
    if (!typeData.frequency) {
      return; // No sound for "other"
    }
    
    setPlayingSound(type.value);
    
    // Create gain node for volume control
    const gainNode = ctx.createGain();
    gainNode.gain.value = 0.1; // 10% volume for comfort
    gainNode.connect(ctx.destination);
    gainNodeRef.current = gainNode;
    
    if (typeData.frequency === 'noise') {
      // White noise
      const bufferSize = 2 * ctx.sampleRate;
      const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1;
      }
      
      const whiteNoise = ctx.createBufferSource();
      whiteNoise.buffer = noiseBuffer;
      whiteNoise.loop = true;
      whiteNoise.connect(gainNode);
      whiteNoise.start(0);
      oscillatorRef.current = whiteNoise;
      
      // Auto-stop after 3 seconds
      setTimeout(() => stopSound(), 3000);
      
    } else if (typeData.frequency === 'click') {
      // Clicking sound - repeated impulses
      let clickCount = 0;
      const clickInterval = setInterval(() => {
        if (clickCount >= 6) {
          clearInterval(clickInterval);
          stopSound();
          return;
        }
        
        const osc = ctx.createOscillator();
        const clickGain = ctx.createGain();
        osc.frequency.value = 2000;
        osc.connect(clickGain);
        clickGain.connect(gainNode);
        
        clickGain.gain.setValueAtTime(0.5, ctx.currentTime);
        clickGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
        
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.05);
        clickCount++;
      }, 500);
      
    } else if (typeData.frequency === 'pulse') {
      // Pulsatile - rhythmic beating
      const osc = ctx.createOscillator();
      osc.frequency.value = 200;
      osc.type = 'sine';
      
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 1.2; // 72 bpm
      
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.3;
      
      lfo.connect(lfoGain);
      lfoGain.connect(gainNode.gain);
      
      osc.connect(gainNode);
      osc.start(0);
      lfo.start(0);
      oscillatorRef.current = osc;
      
      setTimeout(() => stopSound(), 3000);
      
    } else if (typeData.frequency === 'modulated') {
      // Modulated whistle
      const osc = ctx.createOscillator();
      osc.frequency.value = 8000;
      osc.type = 'sine';
      
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.5; // Slow modulation
      
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 1000; // Modulation depth
      
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);
      
      osc.connect(gainNode);
      osc.start(0);
      lfo.start(0);
      oscillatorRef.current = osc;
      
      setTimeout(() => stopSound(), 3000);
      
    } else {
      // Pure tone (high_pitch or low_hum)
      const osc = ctx.createOscillator();
      osc.frequency.value = typeData.frequency;
      osc.type = 'sine';
      osc.connect(gainNode);
      osc.start(0);
      oscillatorRef.current = osc;
      
      // Auto-stop after 3 seconds
      setTimeout(() => stopSound(), 3000);
    }
  };

  const onsetOptions = [
    { value: 'lt_3_months', label: t?.lessThan3Months || 'Moins de 3 mois' },
    { value: '3_6_months', label: t?.threeToSixMonths || '3 à 6 mois' },
    { value: '6_12_months', label: t?.sixToTwelveMonths || '6 mois à 1 an' },
    { value: 'gt_1_year', label: t?.moreThanOneYear || 'Plus d\'un an' },
  ];

  const lateralityOptions = [
    { value: 'left', label: t?.leftEar || 'Gauche', icon: '👈' },
    { value: 'right', label: t?.rightEar || 'Droite', icon: '👉' },
    { value: 'bilateral', label: t?.bothEars || 'Les deux', icon: '👂' },
    { value: 'fluctuating', label: t?.fluctuating || 'Variable', icon: '🔄' },
  ];

  const perceptTypes = [
    { 
      value: 'high_pitch', 
      label: t?.highPitchRinging || 'Sifflement aigu', 
      icon: '📡',
      description: t?.highPitchDesc || 'Son aigu continu (8000-12000 Hz)',
      frequency: 10000
    },
    { 
      value: 'low_hum', 
      label: t?.lowHumming || 'Bourdonnement grave', 
      icon: '🔊',
      description: t?.lowHummingDesc || 'Son grave et profond (100-250 Hz)',
      frequency: 150
    },
    { 
      value: 'clicking', 
      label: t?.clickingCracking || 'Clic/Craquement', 
      icon: '⚡',
      description: t?.clickingDesc || 'Sons secs et répétitifs',
      frequency: 'click'
    },
    { 
      value: 'pulsatile', 
      label: t?.pulsatileBeating || 'Pulsatile (battement)', 
      icon: '❤️',
      description: t?.pulsatileDesc || 'Battement rythmique synchronisé',
      frequency: 'pulse'
    },
    { 
      value: 'modulated', 
      label: t?.modulatedWhistle || 'Sifflement modulé', 
      icon: '〰️',
      description: t?.modulatedDesc || 'Son qui varie en intensité',
      frequency: 'modulated'
    },
    { 
      value: 'white_noise', 
      label: t?.whiteNoiseStatic || 'Bruit blanc/Statique', 
      icon: '📻',
      description: t?.whiteNoiseDesc || 'Bruit de fond continu type "radio"',
      frequency: 'noise'
    },
    { 
      value: 'other', 
      label: t?.other || 'Autre', 
      icon: '❓',
      description: t?.otherDesc || 'Type différent des descriptions ci-dessus',
      frequency: null
    },
  ];

  const togglePercept = (value) => {
    const current = formData.percept_type || [];
    if (current.includes(value)) {
      updateFormData({ percept_type: current.filter(v => v !== value) });
    } else {
      updateFormData({ percept_type: [...current, value] });
    }
  };

  const canGoNext = () => {
    if (subStep === 0) return !!formData.onset_bucket;
    if (subStep === 1) return !!formData.laterality;
    if (subStep === 2) return (formData.percept_type || []).length > 0;
    return false;
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-block p-3 bg-blue-100 rounded-2xl">
          <Ear size={32} className="text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          {t?.tinnitusProfileTitle || 'Votre profil acouphènes'}
        </h2>
        <p className="text-sm text-gray-500">
          {t?.question || 'Question'} {subStep + 1} / 3
        </p>
        {/* Sub-step indicator */}
        <div className="flex justify-center gap-2 pt-2">
          {[0, 1, 2].map((step) => (
            <div
              key={step}
              className={`h-2 rounded-full transition-all ${
                step === subStep
                  ? 'w-8 bg-blue-500'
                  : step < subStep
                  ? 'w-2 bg-blue-300'
                  : 'w-2 bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Sub-step 0: Onset */}
      {subStep === 0 && (
        <div className="space-y-4">
          <p className="text-center text-gray-600 text-lg">
            {t?.howLongTinnitus || 'Depuis combien de temps ressentez-vous votre acouphène ?'}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {onsetOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => updateFormData({ onset_bucket: option.value })}
                className={`p-4 rounded-2xl font-medium text-sm transition-all ${
                  formData.onset_bucket === option.value
                    ? 'bg-blue-500 text-white shadow-lg ring-2 ring-blue-500 ring-offset-2'
                    : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-blue-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sub-step 1: Laterality */}
      {subStep === 1 && (
        <div className="space-y-4">
          <p className="text-center text-gray-600 text-lg">
            {t?.whichSideTinnitus || 'De quel côté ressentez-vous votre acouphène ?'}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {lateralityOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => updateFormData({ laterality: option.value })}
                className={`p-4 rounded-2xl font-medium transition-all ${
                  formData.laterality === option.value
                    ? 'bg-blue-500 text-white shadow-lg ring-2 ring-blue-500 ring-offset-2'
                    : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-blue-300'
                }`}
              >
                <div className="text-2xl mb-1">{option.icon}</div>
                <div className="text-sm">{option.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sub-step 2: Percept Type */}
      {subStep === 2 && (
        <div className="space-y-4">
          <p className="text-center text-gray-600 text-lg font-semibold">
            Quel type de son entendez-vous ?
          </p>
          <p className="text-center text-sm text-gray-500">
            Écoutez les exemples et sélectionnez votre/vos type(s)
          </p>
          
          {/* Scrollable list */}
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
            {perceptTypes.map((type) => {
              const isSelected = (formData.percept_type || []).includes(type.value);
              const isPlaying = playingSound === type.value;
              
              return (
                <div
                  key={type.value}
                  className={`relative rounded-2xl border-2 transition-all ${
                    isSelected
                      ? 'bg-blue-50 border-blue-500 shadow-md'
                      : 'bg-white border-gray-200 hover:border-blue-300'
                  }`}
                >
                  {/* Selection area */}
                  <button
                    onClick={() => togglePercept(type.value)}
                    className="w-full p-4 text-left flex items-start gap-3"
                  >
                    {/* Icon */}
                    <div className="text-3xl flex-shrink-0 mt-1">
                      {type.icon}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold text-base ${
                        isSelected ? 'text-blue-700' : 'text-gray-900'
                      }`}>
                        {type.label}
                      </div>
                      <div className={`text-xs mt-1 ${
                        isSelected ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                        {type.description}
                      </div>
                    </div>
                    
                    {/* Selection indicator */}
                    <div className="flex-shrink-0 mt-2">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        isSelected
                          ? 'bg-blue-500 border-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {isSelected && (
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </button>
                  
                  {/* Play button */}
                  {type.frequency && (
                    <div className="absolute top-4 right-16">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isPlaying) {
                            stopSound();
                          } else {
                            playSound(type);
                          }
                        }}
                        className={`p-2 rounded-full transition-all ${
                          isPlaying
                            ? 'bg-blue-500 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600'
                        }`}
                        title={isPlaying ? 'Arrêter' : 'Écouter l\'exemple'}
                      >
                        {isPlaying ? (
                          <VolumeX size={20} />
                        ) : (
                          <Volume2 size={20} />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Info note */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mt-4">
            <p className="text-xs text-blue-800">
              💡 <strong>Astuce</strong> : Cliquez sur l'icône de haut-parleur pour écouter un exemple sonore de chaque type. Les sons durent 3 secondes.
            </p>
          </div>
        </div>
      )}

      {/* Navigation button - Only "Suivant" for sub-steps, back arrow in header handles going back */}
      {subStep < 2 && (
        <div className="pt-4">
          <button
            onClick={() => setSubStep(subStep + 1)}
            disabled={!canGoNext()}
            className={`w-full py-4 rounded-2xl font-semibold transition-all flex items-center justify-center gap-2 ${
              canGoNext()
                ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Suivant
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default TinnitusProfile;
